import { useCallback, useEffect, useState } from 'react';
import apiService from '../services/api';

const catalogFromApi = (data) =>
  Array.isArray(data?.badges) ? data.badges.map((row) => ({ ...row })) : [];

export default function AchievementBadgesSection({ schoolId, schoolName, hideHeader = false }) {
  const [catalog, setCatalog] = useState([]);
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const rows = catalogFromApi(await apiService.getSchoolAchievementBadges(schoolId));
      setCatalog(rows);
      setSaved(rows);
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || 'Failed to load badges.'
      );
    } finally {
      setLoading(false);
    }
  }, [schoolId]);

  useEffect(() => {
    load();
  }, [load]);

  const selectedCount = catalog.filter((row) => row.selected).length;

  const toggle = (slug) => {
    setSuccess('');
    setCatalog((current) =>
      current.map((row) =>
        row.slug === slug ? { ...row, selected: !row.selected } : row
      )
    );
  };

  const persist = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const slugs = catalog.filter((row) => row.selected).map((row) => row.slug);
      const rows = catalogFromApi(
        await apiService.putSchoolAchievementBadges(schoolId, { slugs })
      );
      setCatalog(rows);
      setSaved(rows);
      setSuccess('Badges saved.');
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || 'Failed to save badges.'
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '32px' }}>
        <p style={{ fontFamily: '"Baikal Book"', fontSize: '14px', color: '#6b7280' }}>
          Loading badges…
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '32px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
      }}
    >
      {hideHeader ? null : (
        <>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '16px',
              alignItems: 'flex-start',
              marginBottom: '8px',
            }}
          >
            <h3
              style={{
                fontFamily: '"Baikal Book"',
                fontSize: '28px',
                fontWeight: 700,
                color: '#111827',
                margin: 0,
              }}
            >
              Badges
            </h3>
          </div>
          <p
            style={{
              fontFamily: '"Baikal Book"',
              fontSize: '14px',
              color: '#6b7280',
              marginBottom: '24px',
            }}
          >
            Select the badges available to users at this school.{' '}
            <span style={{ color: '#6b7280' }}>
              {selectedCount} of {catalog.length} selected
            </span>
          </p>
        </>
      )}
      {hideHeader ? (
        <p
          style={{
            fontFamily: '"Baikal Book"',
            fontSize: '13px',
            color: '#6b7280',
            margin: '0 0 16px',
          }}
        >
          {selectedCount} of {catalog.length} selected
        </p>
      ) : null}

      {error && (
        <div
          style={{
            marginBottom: '16px',
            padding: '12px 14px',
            borderRadius: '8px',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#991b1b',
            fontFamily: '"Baikal Book"',
            fontSize: '13px',
          }}
        >
          {error}
        </div>
      )}
      {success && (
        <div
          style={{
            marginBottom: '16px',
            padding: '12px 14px',
            borderRadius: '8px',
            backgroundColor: '#ecfdf5',
            border: '1px solid #a7f3d0',
            color: '#065f46',
            fontFamily: '"Baikal Book"',
            fontSize: '13px',
          }}
        >
          {success}
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: '12px',
        }}
      >
        {catalog.map((row) => {
          const selected = !!row.selected;
          return (
            <button
              key={row.slug}
              type="button"
              onClick={() => toggle(row.slug)}
              style={{
                textAlign: 'left',
                border: selected ? '2px solid #16a34a' : '1px solid #e5e7eb',
                backgroundColor: selected ? '#ecfdf5' : 'white',
                borderRadius: '12px',
                padding: '16px',
                cursor: 'pointer',
                minHeight: '196px',
                position: 'relative',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  width: '22px',
                  height: '22px',
                  borderRadius: '999px',
                  border: selected ? 'none' : '1px solid #d1d5db',
                  backgroundColor: selected ? '#16a34a' : 'white',
                  color: 'white',
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {selected ? '✓' : ''}
              </span>
              <div
                style={{
                  height: '72px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '12px',
                }}
              >
                {(selected ? row.image_url : row.image_url_locked || row.image_url) ? (
                  <img
                    src={
                      selected
                        ? row.image_url
                        : row.image_url_locked || row.image_url
                    }
                    alt=""
                    style={{ maxHeight: '72px', maxWidth: '72px', objectFit: 'contain' }}
                  />
                ) : (
                  <div
                    style={{
                      width: '56px',
                      height: '64px',
                      borderRadius: '8px',
                      backgroundColor: '#e8e8e8',
                    }}
                  />
                )}
              </div>
              <div
                style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                  fontWeight: 700,
                  color: '#111827',
                }}
              >
                {row.title}
              </div>
              <div
                style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '12px',
                  color: '#6b7280',
                  marginTop: '4px',
                }}
              >
                {row.description}
              </div>
            </button>
          );
        })}
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '16px',
          marginTop: '28px',
        }}
      >
        <p style={{ fontFamily: '"Baikal Book"', fontSize: '13px', color: '#9ca3af', margin: 0 }}>
          Selections apply only to {schoolName || 'this school'}.
        </p>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            type="button"
            disabled={saving}
            onClick={() => {
              setCatalog(saved);
              setSuccess('');
              setError('');
            }}
            style={{
              padding: '10px 18px',
              backgroundColor: 'white',
              color: '#111827',
              border: '1px solid #e5e7eb',
              borderRadius: '10px',
              fontFamily: '"Baikal Book"',
              fontSize: '14px',
              cursor: saving ? 'default' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={persist}
            style={{
              padding: '10px 18px',
              backgroundColor: '#111827',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontFamily: '"Baikal Book"',
              fontSize: '14px',
              cursor: saving ? 'default' : 'pointer',
            }}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
